import FilterSelect from '@/components/base/FilterSelect';
import MarketSelect from '@/components/base/MarketSelect';
import { invoiceStatusTabs } from '@/mocks/invoices';

const statusIcons: Record<string, string> = {
  all: 'ri-stack-line',
  'Pending review': 'ri-time-line',
  'On hold': 'ri-pause-circle-line',
  Approved: 'ri-checkbox-circle-line',
  Paid: 'ri-wallet-3-line',
  Rejected: 'ri-close-circle-line',
};

interface InvoiceFilterBarProps {
  status: string;
  markets: string[];
  query: string;
  counts: Record<string, number>;
  advanceOnly: boolean;
  advanceCount: number;
  blockingOnly: boolean;
  blockingCount: number;
  onStatus: (value: string) => void;
  onSetMarkets: (value: string[]) => void;
  onQuery: (value: string) => void;
  onToggleAdvance: () => void;
  onToggleBlocking: () => void;
}

export default function InvoiceFilterBar({
  status,
  markets,
  query,
  counts,
  advanceOnly,
  advanceCount,
  blockingOnly,
  blockingCount,
  onStatus,
  onSetMarkets,
  onQuery,
  onToggleAdvance,
  onToggleBlocking,
}: InvoiceFilterBarProps) {
  return (
    <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
      <FilterSelect
        value={status}
        onChange={onStatus}
        label="Status"
        className="w-full sm:w-60"
        options={invoiceStatusTabs.map((tab) => ({
          ...tab,
          icon: statusIcons[tab.id],
          count: tab.id === 'all' ? counts.all : counts[tab.id] ?? 0,
        }))}
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <button
          type="button"
          aria-pressed={blockingOnly}
          onClick={onToggleBlocking}
          className={`flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-label font-medium transition-colors ${
            blockingOnly
              ? 'border-primary-500 bg-primary-500 text-background-50'
              : 'border-primary-200 bg-primary-50 text-primary-800 hover:border-primary-400'
          }`}
        >
          <i className="ri-error-warning-line" />
          Blocking checks
          <span
            className={`rounded-full px-1.5 text-[11px] font-semibold ${
              blockingOnly
                ? 'bg-background-50/25 text-background-50'
                : 'bg-background-50 text-primary-800'
            }`}
          >
            {blockingCount}
          </span>
        </button>

        <button
          type="button"
          aria-pressed={advanceOnly}
          onClick={onToggleAdvance}
          className={`flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-label font-medium transition-colors ${
            advanceOnly
              ? 'border-secondary-500 bg-secondary-500 text-background-50'
              : 'border-secondary-200 bg-secondary-100 text-secondary-900 hover:border-secondary-400'
          }`}
        >
          <i className="ri-hand-coin-line" />
          Early payout
          <span
            className={`rounded-full px-1.5 text-[11px] font-semibold ${
              advanceOnly
                ? 'bg-background-50/25 text-background-50'
                : 'bg-background-50 text-secondary-900'
            }`}
          >
            {advanceCount}
          </span>
        </button>

        <MarketSelect
          value={markets}
          onChange={onSetMarkets}
          placeholder="All markets"
          compact
          className="sm:w-56"
        />

        <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-foreground-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search invoice, creator or campaign..."
            className="h-9 w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none sm:w-64"
          />
        </div>
      </div>
    </div>
  );
}